import { clsx } from 'clsx'
import { IconButton } from 'component/Button'
import { useUi } from 'component/Context/UiContext'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { useBodyScrollLock } from 'hook/useBodyScrollLock'
import { useLatestCallback } from 'hook/useLatestCallback'
import { useRefState } from 'hook/useRefState'
import { Icon } from 'icon/Icon'
import { MouseEvent, ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { styles } from 'util/style'
import css from './Modal.module.scss'

type Props = {
  children: ReactNode
  className?: string
  rootClassName?: string
  onCancel?(): void
  open?: boolean
  pure?: boolean
  title?: ReactNode
  width?: number
  white?: boolean
  fullscreen?: boolean
  closeButton?: boolean
  oh?: boolean
  banner?: boolean
}

export const Modal = (props: Props) => {
  const {
    children,
    onCancel,
    rootClassName,
    pure,
    title,
    width,
    white,
    fullscreen,
    closeButton = true,
    oh,
    banner,
  } = props
  const { tsum } = useUi()
  const open = props.open !== false
  const [ref, setRef] = useRefState<HTMLDivElement>()
  const backdrop = useRef<HTMLDivElement>(null)

  useBodyScrollLock(open)

  const classNameRoot = clsx(
    css.root, rootClassName,
    white ? css.white : css.black,
  )
  const className = clsx(
    props.className,
    css.modal,
    pure ? css.pure : css.bordered,
    title && css.titled,
    fullscreen && css.fullscreen,
    banner && css.banner,
  )
  const style = styles({ width })

  useEffect(() => {
    if (!ref) return
    if (open) {
      const input = ref.querySelector('input')
      if (input) setTimeout(() => input?.select(), 10)
    }
  }, [ref, open])

  const onRef = useLatestCallback((element: HTMLDivElement | null) => {
    setRef(element)
  })

  const onClickClose = useLatestCallback((event: MouseEvent) => {
    if (event.button === 0) {
      if (onCancel) onCancel()
    }
  })

  const onBackdrop = useLatestCallback((event: MouseEvent) => {
    if (event.target === backdrop.current && onCancel) onCancel()
  })

  const header = title ? (
    <Space className={clsx(css.header, closeButton && css.closable)}>
      <Typo className={css.title} size={tsum ? 17 : 15} medium={!tsum} accent={tsum} wrap>
        {title}
      </Typo>
      {closeButton && (
        <IconButton size={48} className={css.cross} action={onClickClose}>
          <Icon name="close" size={20} />
        </IconButton>
      )}
    </Space>
  ) : undefined

  return createPortal(
    <div
      className={classNameRoot}
      style={{
        display: open ? 'flex' : 'none',
      }}
    >
      {open && <div ref={backdrop} className={css.backdrop} onMouseDown={onBackdrop} />}
      <div ref={onRef} className={className} style={style} data-modal>
        <div className={clsx(css.content, oh && css.oh)}>
          {header}
          {children}
        </div>
        {!title && closeButton && (
          <IconButton className={css.close} action={onClickClose}>
            <Icon name="close" size={20} />
          </IconButton>
        )}
      </div>
    </div>,
    document.body,
  )
}
